import React, {  useMemo, useState } from "react";
import { Controller, useController } from "react-hook-form";
import { CCollapse } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import Dropdown from "../../../../components/multiSelectDropdown/multiSelectDropdown";

const USER_TYPES = [
  { id: "owner", name: "Owner" },
  { id: "corporate", name: "Corporate" },
  { id: "multi_organization_owner", name: "Multi Organizations Owner" },
  { id: "multi_organization_corporate", name: "Multi Organizations Corporate" }
];

const CORPORATE_USER_TYPES = ["corporate", "multi_organization_corporate"];

export const AccessPermissions = ({
  isAdmin,
  watchedValues: { isMultiOrg, userType },
  control,
  setValue,
  organizations,
  canEditLocations,
  canChangePermissions,
  allPermissions
}) => {
  const permissionsLocked = !canChangePermissions;
  const locationsDisabled = CORPORATE_USER_TYPES.includes(userType);

  const selectedUserType = USER_TYPES.find((type) => type.id === userType) ?? null;

  const onUserTypeChange = (option) => {
    setValue("user_type", option.id, { shouldDirty: true });
  };

  const [permissionTypeKey, setPermissionTypeKey] = useState("");
  const [currentGroupName, setCurrentGroupName] = useState("");


  const { field: locations } = useController({ control, name: "clubready_store_ids" });

  const onPermissionTypeClick = (type) => {
    setPermissionTypeKey(type);

    if (type === permissionTypeKey) {
      setPermissionTypeKey("");
      setCurrentGroupName("");
    }
  };

  const onGroupPermissionClick = (e) => {
    let targetText = e.target.innerText;
    let label = e.target.parentElement.dataset.label;
    let groupName = label ? label + "-" + targetText : targetText;

    if (groupName === currentGroupName) {
      setCurrentGroupName("");
      return;
    }

    setCurrentGroupName(groupName);
  };

  const onPermissionClick = (e, state) => {
    e.stopPropagation();
    let checked = e.target.checked;
    let val = +e.target.value;

    if (!checked) {
      delete state.value[val];
      state.onChange(state.value);
      return;
    }

    state.onChange({ ...state.value, [val]: checked });
  };

  const onAllClick = ({ e, options, state }) => {
    let currentPermissions = options.map((p) => p.id);
    let checked = e.target.checked;

    if (checked) {
      let ids = currentPermissions.reduce((acc, val) => ({ ...acc, [val]: true }), {});
      state.onChange({ ...state.value, ...ids });
    } else {
      for (let key in state.value) {
        if (currentPermissions.includes(+key)) {
          delete state.value[key];
        }
      }

      state.onChange(state.value);
    }
  };

  const isAllChecked = (options, state) => {
    let allPermissionIdsPerGroup = options.map((permission) => permission.id);
    return allPermissionIdsPerGroup.every((id) => state.value[id]);
  };

  const renderInnerControls = ({ id, group, options, state }) => (
    <>
      <p style={{ fontWeight: "bold" }}>
        Selected Locations: {options.filter((option) => state.value[option.id]).length}
      </p>
      <div className="admin-users__location-list" onClick={(e) => e.stopPropagation()}>
        <div className="form-check" key={group}>
          <input
            className="form-check-input"
            type="checkbox"
            value={group}
            id={id ? id : group}
            disabled={!canEditLocations}
            onChange={(e) => onAllClick({ e, options, state })}
            checked={isAllChecked(options, state)}
          />
          <label className="form-check-label" htmlFor={id ? id : group}>
            Check All / Uncheck All
          </label>
        </div>

        {options.map((permission) => (
          <div className="form-check" key={permission.id}>
            <input
              className="form-check-input"
              type="checkbox"
              value={permission.id}
              id={permission.id}
              disabled={!canEditLocations}
              onChange={(e) => onPermissionClick(e, state)}
              checked={state.value[permission.id] ?? false}
            />
            <label className="form-check-label" htmlFor={permission.id}>
              {permission.name}
            </label>
          </div>
        ))}
      </div>
    </>
  );

  const locationOptions = allPermissions.location_permissions || [];
  const locationOptionsKey = locationOptions.map((location) => location.id).join(",");
  const reorderedLocations = useMemo(() => {
    let selected = [];
    let rest = [];

    for (let location of locationOptions) {
      if (locations.value[location.id]) {
        selected.push(location);
      } else {
        rest.push(location);
      }
    }

    return [...selected, ...rest];
  }, [locationOptionsKey]);

  const renderGroups = (groupParent) => (
    <div onClick={(e) => onGroupPermissionClick(e)}>
      <CCollapse show={!permissionsLocked && !locationsDisabled && groupParent === permissionTypeKey}>
        {renderInnerControls({
          group: groupParent,
          options: reorderedLocations,
          state: locations
        })}
      </CCollapse>
    </div>
  );
  
  return (
    <>
      <div className="admin-users__info admin-users__settings">
        <div className="admin-users__section">
          <div className="admin-users__dropdown">
            <p>User Type</p>
            <Dropdown
              height={275}
              haveOnSelectChange
              options={USER_TYPES}
              selectedFilters={selectedUserType}
              numberDisplayed={0}
              onSelectChange={onUserTypeChange}
            />
          </div>

          {isMultiOrg && (
            <div className="admin-users__dropdown">
              <p>Organizations</p>
              <Controller
                control={control}
                name="organization_ids"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Dropdown
                      includeSelectAll
                      enableOnly
                      height={275}
                      haveOnSelectChange
                      options={organizations}
                      selectedFilters={value}
                      numberDisplayed={0}
                      onSelectChange={(data) => onChange(data)}
                    />
                  );
                }}
              />
            </div>
          )}

          {!isMultiOrg && !isAdmin && (
            <div className="admin-users__dropdown">
              <p>Organization</p>
              <Controller
                control={control}
                name="organization_id"
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <Dropdown
                      onBlur={onBlur}
                      height={275}
                      haveOnSelectChange
                      options={organizations}
                      selectedFilters={value}
                      numberDisplayed={0}
                      onSelectChange={(data) => {
                        onChange(data);
                      }}
                    />
                  );
                }}
              />
            </div>
          )}
          <div className="admin-users__empty" />
        </div>

        {(locationOptions.length > 0) && (
          <>
            <div
              className="group__item"
              style={
                locationsDisabled || permissionsLocked
                  ? { cursor: "not-allowed", opacity: 0.5 }
                  : null
              }
              onClick={() =>
                !locationsDisabled &&
                !permissionsLocked &&
                onPermissionTypeClick("location_permissions")
              }
              data-tooltip-id={
                locationsDisabled || permissionsLocked
                  ? "locationPermissionsTooltip"
                  : null
              }
              data-tooltip-content={
                locationsDisabled
                  ? "Locations list hidden for Corporate/Multi-Org Corporate (full access granted by default)"
                  : permissionsLocked
                    ? "Save changes to edit permissions"
                    : null
              }
            >
              Choose Location Permissions
            </div>
            {renderGroups("location_permissions")}
            {(locationsDisabled || permissionsLocked) && (
              <Tooltip id="locationPermissionsTooltip" place="top" />
            )}
          </>
        )}
      </div>
    </>
  );
};
